import * as React from 'react'
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react'

import { UnderlineInput } from '@/components/ui/underline-input'

export interface SearchOption {
  id: number | string | null
  name: string
  /** Optional flag to indicate this option represents creating a new item. */
  isNew?: boolean
  /**
   * Muted secondary text on the dropdown row (e.g. an Omni ID) — shown to
   * disambiguate while picking, never part of the input text.
   */
  hint?: string
}

export interface SearchProps {
  options: SearchOption[]
  value: SearchOption | null
  onChange: (option: SearchOption | null) => void
  ariaLabel?: string
  onQueryChange?: (query: string) => void
  isLoading?: boolean
  errorMessage?: string
  lockAfterSelect?: boolean
  allowCreateOption?: boolean
  disabled?: boolean
}

export function Search(props: SearchProps) {
  const {
    options,
    value,
    onChange,
    ariaLabel = 'Search',
    onQueryChange,
    isLoading = false,
    errorMessage,
    lockAfterSelect = false,
    allowCreateOption = true,
    disabled = false,
  } = props

  const selectedExisting = Boolean(value && !value.isNew && value.id != null)

  const [query, setQuery] = React.useState('')
  const [isLocked, setIsLocked] = React.useState(
    lockAfterSelect && selectedExisting,
  )

  React.useEffect(() => {
    if (!lockAfterSelect) {
      setIsLocked(false)
      return
    }

    if (selectedExisting) {
      setIsLocked(true)
      return
    }

    setIsLocked(false)
  }, [lockAfterSelect, selectedExisting])

  const handleQueryChange = (next: string) => {
    setQuery(next)
    onQueryChange?.(next)
  }

  const queryTrimmed = query.trim()

  const filteredOptions =
    onQueryChange || queryTrimmed === ''
      ? options
      : options.filter((option) =>
          option.name.toLowerCase().includes(queryTrimmed.toLowerCase()),
        )

  // The selected option stays an exact match even when the fetched list no
  // longer contains it. Composed display names ("Josh – OMNI123") feed the
  // whole string back as the search query, which matches no fullName or
  // externalId server-side and empties `options` — without this, blurring
  // would silently convert the selection into a "new customer" whose name
  // is the composed text.
  const selectedAsExactMatch = (typed: string): SearchOption | undefined =>
    value &&
    !value.isNew &&
    value.id != null &&
    (value.name?.trim().toLowerCase() ?? '') === typed.toLowerCase()
      ? value
      : undefined

  const exactExistingOption =
    selectedAsExactMatch(queryTrimmed) ??
    options.find(
      (option) => option.name.toLowerCase() === queryTrimmed.toLowerCase(),
    )
  const hasExactExisting = Boolean(exactExistingOption)

  const hasCreateOption =
    allowCreateOption && queryTrimmed.length > 0 && !hasExactExisting

  const createOption: SearchOption | null = hasCreateOption
    ? { id: null, name: queryTrimmed, isNew: true }
    : null

  const handleSelect = (option: SearchOption | null) => {
    if (option === null && query.trim().length > 0) {
      return
    }

    onChange(option)

    if (option) {
      handleQueryChange(option.name)
    }

    if (lockAfterSelect && option && !option.isNew && option.id != null) {
      setIsLocked(true)
    } else {
      setIsLocked(false)
    }
  }

  const handleInputChange = (next: string) => {
    handleQueryChange(next)

    if (isLocked) return

    if (value && !value.isNew && value.id != null) {
      const currentName = value.name?.trim() ?? ''
      if (next.trim() !== currentName) {
        onChange(null)
      }
    }

    if (next.trim().length === 0 && value?.isNew) {
      onChange(null)
    }
  }

  const commitBlurValue = (rawValue?: string) => {
    if (isLocked) return

    const typed = (rawValue ?? query).trim()
    if (typed.length === 0) {
      onChange(null)
      return
    }

    const exactMatch =
      selectedAsExactMatch(typed) ??
      options.find(
        (option) => option.name.trim().toLowerCase() === typed.toLowerCase(),
      )

    if (exactMatch) {
      onChange(exactMatch)
      handleQueryChange(exactMatch.name)
      return
    }

    if (!allowCreateOption) {
      onChange(null)
      return
    }

    const nextNewOption: SearchOption = {
      id: null,
      name: typed,
      isNew: true,
    }
    onChange(nextNewOption)
    handleQueryChange(typed)
  }

  const draftName =
    queryTrimmed.length > 0
      ? queryTrimmed
      : value?.isNew
        ? value.name.trim()
        : ''

  const hasDraftNewCustomerIntent =
    allowCreateOption &&
    draftName.length > 0 &&
    !options.some(
      (option) => option.name.toLowerCase() === draftName.toLowerCase(),
    )

  const helperTone = selectedExisting
    ? 'text-emerald-700'
    : hasDraftNewCustomerIntent
      ? 'text-amber-700'
      : 'text-slate-500'

  const helperText = selectedExisting
    ? 'Using existing customer. Click Edit to change.'
    : hasExactExisting
      ? 'Matches an existing customer — press Enter to use existing.'
      : hasDraftNewCustomerIntent
        ? 'New customer will be created on save.'
        : 'Type to search existing customers.'

  if (disabled) {
    return (
      <UnderlineInput
        value={value ? value.name : ''}
        readOnly
        className="cursor-not-allowed bg-slate-50 text-slate-500"
      />
    )
  }

  return (
    <Combobox
      as="div"
      className="relative"
      value={value}
      onChange={handleSelect}
    >
      <div className="max-w-sm">
        <div className="flex items-center gap-2">
          <ComboboxInput
            as={UnderlineInput}
            className={`flex-1 ${isLocked ? 'bg-slate-100 text-slate-700 shadow-[0_1px_0_0_rgba(148,163,184,1)]' : ''}`}
            aria-label={ariaLabel}
            data-testid="customer-search-input"
            autoComplete="new-password"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            displayValue={(option: SearchOption | null) => option?.name ?? ''}
            value={isLocked ? value?.name ?? '' : query || value?.name || ''}
            readOnly={isLocked}
            disableAutoSelect={isLocked}
            onChange={(event) => {
              handleInputChange(event.target.value)
            }}
            onBlur={(event) => {
              commitBlurValue(event.currentTarget.value)
            }}
          />
          {isLocked && (
            <button
              type="button"
              className="inline-flex h-7 items-center rounded-md border border-slate-300 bg-white px-2 text-[11px] uppercase tracking-wide text-slate-700 hover:bg-slate-50"
              onClick={() => {
                setIsLocked(false)
                handleQueryChange(value?.name ?? '')
                onChange(value)
              }}
            >
              Edit
            </button>
          )}
        </div>

        <p className={`mt-1 text-xs ${helperTone}`}>
          {selectedExisting ? '🔒 ' : ''}
          {helperText}
        </p>
      </div>

      <ComboboxOptions
        data-testid="customer-search-options"
        className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-slate-200 bg-white text-sm text-slate-900 shadow-lg empty:invisible"
      >
        {isLoading && (
          <div className="cursor-default px-2 py-1 text-slate-500">
            Loading...
          </div>
        )}

        {errorMessage && (
          <div className="cursor-default px-2 py-1 text-amber-700">
            {errorMessage}
          </div>
        )}

        {hasCreateOption && createOption && (
          <ComboboxOption
            value={createOption}
            className="cursor-pointer px-2 py-1 text-slate-900 data-focus:bg-blue-100"
          >
            <span className="font-medium text-amber-700">
              Create new customer “{queryTrimmed}”
            </span>
          </ComboboxOption>
        )}

        {filteredOptions.map((option) => (
          <ComboboxOption
            key={option.id ?? option.name}
            value={option}
            className="cursor-pointer px-2 py-1 text-slate-900 data-focus:bg-blue-100"
          >
            {option.name}
            {option.hint ? (
              <span className="ml-2 text-xs text-slate-400">{option.hint}</span>
            ) : null}
          </ComboboxOption>
        ))}
      </ComboboxOptions>
    </Combobox>
  )
}

export default Search
